import React, { useState, useEffect } from "react";
import { Edit, Trash2, Plus, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./Department.css";

const url = process.env.REACT_APP_BACKEND_URL;

const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const endpoint = showInactive ? `${url}/api/department/all` : `${url}/api/department`;
      const res = await axios.get(endpoint);
      setDepartments(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this department?")) {
      try {
        const res = await axios.delete(`${url}/api/department/${id}`);
        toast.success(res.data.message || "Department deactivated successfully");
        fetchDepartments();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to deactivate department");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this department?")) {
      try {
        const res = await axios.put(`${url}/api/department/${id}/restore`);
        toast.success(res.data.message || "Department activated successfully");
        fetchDepartments();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to activate department");
      }
    }
  };

  if (loading) return <div className="loading">Loading departments...</div>;

  return (
    <div className="department-container">
      <div className="header">
        <h2>Departments List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button
            onClick={() => navigate("/departments/add")}
            className="add-button"
          >
            <Plus size={16} /> Add Department
          </button>
        </div>
      </div>

      <div className="department-list">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>District</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  No departments found
                </td>
              </tr>
            ) : (
              departments.map((department) => (
                <tr key={department._id} className={department.status === 'inactive' ? 'inactive-row' : ''}>
                  <td>{department.name}</td>
                  <td>{department.district?.name || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${department.status}`}>
                      {department.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <div className="actions">
                      {department.status === 'active' ? (
                        <>
                          <button
                            onClick={() => navigate(`/departments/edit/${department._id}`)}
                            className="edit-btn"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(department._id)}
                            className="delete-btn"
                            title="Deactivate"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestore(department._id)}
                          className="restore-btn"
                          title="Activate"
                        >
                          <RotateCcw size={14} /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DepartmentList;